import { useEffect, useMemo, useState } from "react";
import { CircularProgress } from "@mui/material";
import classes from "../homePageNew.module.css";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

// Scores are rounded to whole numbers BEFORE subtracting (same as the old
// Home page's alert engine). Any change is reported — no minimum size — for
// the Index/Visibility/Sentiment bullets and for every individual Brand
// Pillar / Purchase Criteria attribute. An attribute is only compared when
// both runs have a non-zero value: a 0 means "not measured", not "dropped
// to nothing".

// Brief pause on every combination/LLM change so the section visibly
// refreshes rather than silently swapping text.
const RECOMPUTE_MS = 300;

const round = (v) => Math.round(Number(v) || 0);

function mainBrandOf(point) {
  return point?.brands?.find((b) => b.isMainBrand) || null;
}

// One score metric (Index / Visibility / Sentiment) → one bullet. The text
// always states the actual change ("increased by 3" / "decreased by 4"),
// and only says "consistent" when the score didn't move at all. The dot
// simply follows the direction: green for an increase (or no change), red
// for any decrease.
function scoreBullet(label, current, previous) {
  if (current == null || previous == null) return null;
  const cur = round(current);
  const prev = round(previous);
  const delta = cur - prev;
  if (delta === 0) return { tone: "positive", text: `${label} is consistent, not changed from the previous run (${cur}).` };
  const verb = delta > 0 ? "increased" : "decreased";
  return { tone: delta < 0 ? "negative" : "positive", text: `${label} ${verb} by ${Math.abs(delta)}.` };
}

// "A", "A and B", "A, B and C"
function joinNames(names) {
  if (names.length <= 1) return names.join("");
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

// Brand Pillars / Purchase Criteria → ONE statement naming every attribute
// that changed, with direction and size. Attributes that moved by the same
// amount in the same direction are combined into one clause, e.g.
// "Brand Pillar: Quality decreased by 2; Trustworthiness and Value
// decreased by 1; Price increased by 1." Drops are listed first (biggest
// first), then gains. Red if anything dropped, otherwise green; "no
// change" when every comparable attribute held steady.
function attributeBullet(groupLabel, currentAttrs, previousAttrs) {
  if (!currentAttrs || !previousAttrs) return null;
  const changes = [];
  Object.keys(currentAttrs).forEach((name) => {
    const cur = round(currentAttrs[name]);
    const prev = round(previousAttrs[name]);
    if (!cur || !prev) return;
    const delta = cur - prev;
    if (delta !== 0) changes.push({ name, delta });
  });

  if (changes.length === 0) return { tone: "positive", text: `${groupLabel}: no attribute changed from the previous run.` };

  // Bucket by signed delta so equal changes share one clause.
  const buckets = new Map();
  changes.forEach((c) => {
    if (!buckets.has(c.delta)) buckets.set(c.delta, []);
    buckets.get(c.delta).push(c.name);
  });
  const deltas = [...buckets.keys()];
  const drops = deltas.filter((d) => d < 0).sort((a, b) => a - b);
  const gains = deltas.filter((d) => d > 0).sort((a, b) => b - a);
  const clauses = [...drops, ...gains].map((d) => `${joinNames(buckets.get(d))} ${d < 0 ? "decreased" : "increased"} by ${Math.abs(d)}`);

  return { tone: drops.length > 0 ? "negative" : "positive", text: `${groupLabel}: ${clauses.join("; ")}.` };
}

function buildInsights(provider, isProduct) {
  const series = provider?.series || [];
  if (series.length < 2) return { bullets: [], transition: null };

  const latest = series[series.length - 1];
  const previous = series[series.length - 2];
  const cur = mainBrandOf(latest);
  const prev = mainBrandOf(previous);
  if (!cur || !prev) return { bullets: [], transition: null };

  // "gpt-4o → gpt-5.2" when the model version changed between the two
  // runs (matched by provider, so an upgrade still compares), else just
  // the model.
  const transition = previous.llmName !== latest.llmName ? `${previous.llmName} → ${latest.llmName}` : latest.llmName;

  const pillarLabel = (isProduct ? ENTITY_LABELS.productDimension : ENTITY_LABELS.brandPillar).replace(/s$/, "");
  const criteriaLabel = ENTITY_LABELS.purchaseCriteria;

  const bullets = [
    scoreBullet("Brand Index Score", cur.indexScore, prev.indexScore),
    scoreBullet("Visibility Score", cur.visibilityScore, prev.visibilityScore),
    scoreBullet("Sentiment Score", cur.sentimentScore, prev.sentimentScore),
    attributeBullet(pillarLabel, cur.corePillars, prev.corePillars),
    attributeBullet(criteriaLabel, cur.purchaseCriteria, prev.purchaseCriteria),
  ].filter(Boolean);

  return { bullets, transition };
}

/**
 * KEY INSIGHTS · VS PREVIOUS RUN — computed entirely client-side from the
 * selected LLM's latest two runs (monitoringDerive.js series), so it
 * recomputes on the fly whenever the combination or LLM changes. One
 * bullet per metric (Brand Index, Visibility, Sentiment, Brand Pillar,
 * Purchase Criteria); red = alert-level drop, green = up or consistent.
 * The LLM comes from the single picker above the chart (MonitoringModule),
 * which drives both the chart and this section.
 */
export default function MonitoringInsights({ providerLlms, chartProvider, isProduct, resetKey }) {
  const activeProvider = providerLlms.find((p) => p.provider === chartProvider) || providerLlms[0];

  const [computing, setComputing] = useState(true);
  useEffect(() => {
    setComputing(true);
    const timer = setTimeout(() => setComputing(false), RECOMPUTE_MS);
    return () => clearTimeout(timer);
  }, [resetKey, activeProvider?.provider]);

  const { bullets, transition } = useMemo(() => buildInsights(activeProvider, isProduct), [activeProvider, isProduct]);

  return (
    <div className={classes.monInsightsCard}>
      <div className={classes.monInsightsTitle}>KEY INSIGHTS · VS PREVIOUS RUN</div>
      <div className={classes.monInsightsList}>
        {computing ? (
          <div className={classes.monInsightsLoading}>
            <CircularProgress size={20} />
          </div>
        ) : bullets.length === 0 ? (
          <div className={classes.monInsightsEmpty}>Not enough run history yet — insights appear once this LLM has at least two runs.</div>
        ) : (
          bullets.map((row, i) => (
            <div className={classes.monInsightRow} key={i}>
              <span className={`${classes.monInsightDot} ${row.tone === "positive" ? classes.monInsightDotPositive : classes.monInsightDotNegative}`} />
              <span>
                <b>{transition}</b> - {row.text}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
